import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import {
  setAuthToken,
  warmupOfflineData,
  getOfflineWarmupIntervalMs,
  getOfflineWarmupDelayMs,
  isOfflineWarmupEnabled,
  safeLocalStorageSetItem,
  attemptSilentRefresh,
  ensureFreshAccessToken,
  clearOfflineQueueOnSessionEnd
} from './api';
import { createSseReconnectHandler, SSE_CHANNELS } from './deployResilience';
import { installBackgroundAppSyncWatchers, APP_SYNC_EVENTS } from './backgroundAppSync';
import { isDev } from '../utils/dev';
import i18n from '../i18n';
import {
  applyUserPreferences,
  clearUserPreferencesCache
} from './userPreferences';
import { primeActorAvatarRegistry } from '../utils/actorAvatarCache';
import {
  buildNotificationAvatarViewer,
  ensureActorAvatarProfilesPrimed,
  resetActorAvatarProfilesPrime
} from '../utils/actorAvatarRegistry';

// 🔇 Logger silencieux en production (éliminé par Terser via process.env.NODE_ENV = 'production')
// eslint-disable-next-line no-unused-vars
const devLog = process.env.NODE_ENV !== 'production' ? console.log.bind(console) : () => {};
const DEFAULT_INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000;
const configuredTimeoutMs = Number(process.env.REACT_APP_INACTIVITY_TIMEOUT_MS);
const INACTIVITY_TIMEOUT_MS = process.env.NODE_ENV === 'production'
  ? DEFAULT_INACTIVITY_TIMEOUT_MS
  : (Number.isFinite(configuredTimeoutMs) && configuredTimeoutMs > 0
    ? configuredTimeoutMs
    : DEFAULT_INACTIVITY_TIMEOUT_MS);
const AUTO_LOGOUT_ENABLED = false;
/** Renouvellement JWT avant expiration (access token 15 min). */
const PROACTIVE_REFRESH_INTERVAL_MS = 12 * 60 * 1000;
const JWT_REFRESH_BUFFER_SEC = 90;

const setSseCompatibilityCookie = (token) => {
  if (!token) return;
  const secureAttr = window.location.protocol === 'https:' ? '; Secure' : '';
  // Cookie JS de compatibilité SSE: utile si le Set-Cookie backend n'est pas propagé via proxy.
  document.cookie = `ucp_token=${encodeURIComponent(token)}; path=/; SameSite=Lax${secureAttr}`;
};

const parseJwtPayload = (token) => {
  try {
    const parts = String(token || '').split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = decodeURIComponent(Array.prototype.map.call(atob(padded), (c) => (
      `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`
    )).join(''));
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const isJwtExpired = (token, bufferSec = 0) => {
  const payload = parseJwtPayload(token);
  const exp = Number(payload?.exp);
  if (!Number.isFinite(exp)) return false;
  const nowSec = Math.floor(Date.now() / 1000);
  return exp <= nowSec + bufferSec;
};

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]); // ✅ NOUVEAU: Notifications SSE
  const offlineSyncSummaryRef = useRef({ count: 0, timer: null });
  const logoutInProgressRef = useRef(false);

  const dismissNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const pushNotification = useCallback((notification) => {
    const normalized = {
      id: notification.id || `notif-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type: notification.type || 'info',
      title: notification.title || i18n.t('toast.information'),
      message: notification.message || '',
      timestamp: notification.timestamp || new Date(),
      duration: notification.duration || 0,
      persistent: Boolean(notification.persistent)
    };

    setNotifications((prev) => {
      const idx = prev.findIndex((n) => n.id === normalized.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = normalized;
        return next;
      }
      return [...prev, normalized];
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const clearStoredSession = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      try { sessionStorage.removeItem('token'); } catch (_) {}
      try { sessionStorage.removeItem('user'); } catch (_) {}
      setAuthToken(null);
      clearOfflineQueueOnSessionEnd();
    };

    const hydrateUserFromStorage = () => {
      const raw = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    };

    const applySession = (userData, token) => {
      if (token) {
        if (!safeLocalStorageSetItem('token', token)) {
          try { sessionStorage.setItem('token', token); } catch { /* ignore */ }
        }
        setAuthToken(token);
        setSseCompatibilityCookie(token);
      }
      if (userData) {
        const serialized = JSON.stringify(userData);
        if (!safeLocalStorageSetItem('user', serialized)) {
          try { sessionStorage.setItem('user', serialized); } catch { /* ignore */ }
        }
        setUser(userData);
        ensureActorAvatarProfilesPrimed(buildNotificationAvatarViewer(userData));
      }
    };

    const bootstrapAuth = async () => {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      let userData = hydrateUserFromStorage();

      if (token && userData && !isJwtExpired(token, JWT_REFRESH_BUFFER_SEC)) {
        applySession(userData, token);
        if (!cancelled) setLoading(false);
        return;
      }

      const shouldTryRefresh = Boolean(
        userData || token || document.cookie.includes('ucp_refresh=')
      );

      if (shouldTryRefresh) {
        try {
          const newToken = await attemptSilentRefresh();
          userData = hydrateUserFromStorage() || userData;
          if (newToken && userData) {
            applySession(userData, newToken);
            if (!cancelled) setLoading(false);
            return;
          }
          // Lot 6 (S4) — refresh OK mais profil absent : éviter ping-pong token / 401.
          if (newToken && !userData) {
            clearStoredSession();
            if (!cancelled) setLoading(false);
            return;
          }
        } catch (error) {
          devLog('⚠️ [AuthContext] Refresh silencieux au démarrage impossible:', error?.message || error);
        }
      }

      if (token && isJwtExpired(token) && !userData) {
        clearStoredSession();
        if (!cancelled) setLoading(false);
        return;
      }

      if (token && !userData && isDev()) {
        try {
          const p = parseJwtPayload(token);
          if (p) {
            const minimal = {
              id: p.id || p.sub || null,
              email: p.email || null,
              role: p.role || null,
              name: p.name || null
            };
            applySession(minimal, token);
            if (!cancelled) setLoading(false);
            return;
          }
        } catch { /* ignore */ }
      }

      if (token && isJwtExpired(token)) {
        clearStoredSession();
      }

      if (!cancelled) {
        const finalToken = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!finalToken || isJwtExpired(finalToken, JWT_REFRESH_BUFFER_SEC)) {
          clearOfflineQueueOnSessionEnd();
        }
        setLoading(false);
      }
    };

    bootstrapAuth();

    return () => {
      cancelled = true;
    };
  }, []);

  // ✅ OFFLINE-002 : Notifications globales mode hors-ligne + queue + synchro
  useEffect(() => {
    const OFFLINE_MODE_NOTIFICATION_ID = 'ucp-offline-mode';
    const syncSummaryState = offlineSyncSummaryRef.current;

    const flushSyncSummary = () => {
      const count = syncSummaryState.count;
      syncSummaryState.count = 0;
      syncSummaryState.timer = null;

      if (count > 0) {
        pushNotification({
          id: `ucp-offline-sync-success-${Date.now()}`,
          type: 'success',
          title: i18n.t('toast.syncSuccess'),
          message: count > 1
            ? `${count} actions hors-ligne ont été synchronisées.`
            : '1 action hors-ligne a été synchronisée.',
          duration: 5000
        });
      }
    };

    const scheduleSyncSummary = () => {
      if (syncSummaryState.timer) {
        clearTimeout(syncSummaryState.timer);
      }
      syncSummaryState.timer = setTimeout(flushSyncSummary, 1200);
    };

    const handleOffline = () => {
      pushNotification({
        id: OFFLINE_MODE_NOTIFICATION_ID,
        type: 'warning',
        title: i18n.t('toast.offlineMode'),
        message: 'Vous êtes hors-ligne. Les données en cache restent accessibles.',
        persistent: true
      });
    };

    const handleOnline = () => {
      dismissNotification(OFFLINE_MODE_NOTIFICATION_ID);
      pushNotification({
        id: `ucp-online-${Date.now()}`,
        type: 'success',
        title: i18n.t('toast.connectionRestored'),
        message: 'Synchronisation des actions en attente en cours...',
        duration: 3500
      });
    };

    const handleQueued = (event) => {
      const method = event?.detail?.method?.toUpperCase() || 'ACTION';
      pushNotification({
        id: `ucp-offline-queued-${event?.detail?.id || Date.now()}`,
        type: 'info',
        title: i18n.t('toast.actionPending'),
        message: `${method} sera envoyé automatiquement au retour réseau.`,
        duration: 5000
      });
    };

    const handleSyncSuccess = () => {
      syncSummaryState.count += 1;
      scheduleSyncSummary();
    };

    const handleSyncDropped = (event) => {
      const status = event?.detail?.status;
      const serverMsg = event?.detail?.message;
      const method = String(event?.detail?.method || '').toUpperCase();
      const parts = [];
      if (serverMsg) parts.push(serverMsg);
      else if (status != null) {
        parts.push(`Réponse serveur HTTP ${status}.`);
        if (method === 'DELETE' && status === 403) {
          parts.push('Vérifiez vos droits sur cette ressource.');
        }
      }
      if (parts.length === 0) {
        parts.push('Une action en attente n’a pas pu être appliquée côté serveur.');
      }
      pushNotification({
        id: `ucp-offline-sync-dropped-${Date.now()}`,
        type: 'warning',
        title: i18n.t('toast.actionNotSynced'),
        message: parts.join(' '),
        duration: 6500
      });
    };

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      handleOffline();
    }

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    window.addEventListener('ucp-offline-queued', handleQueued);
    window.addEventListener('ucp-offline-sync-success', handleSyncSuccess);
    window.addEventListener('ucp-offline-sync-dropped', handleSyncDropped);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('ucp-offline-queued', handleQueued);
      window.removeEventListener('ucp-offline-sync-success', handleSyncSuccess);
      window.removeEventListener('ucp-offline-sync-dropped', handleSyncDropped);

      if (syncSummaryState.timer) {
        clearTimeout(syncSummaryState.timer);
        syncSummaryState.timer = null;
      }
      syncSummaryState.count = 0;
    };
  }, [dismissNotification, pushNotification]);

  // ✅ NOUVEAU: SSE Listener pour les événements client
  useEffect(() => {
    if (!user || user.role !== 'client') {
      return; // SSE uniquement pour les clients
    }

    let eventSource = null;
    let retryCount = 0;
    let retryTimer = null;
    let isActive = true;
    let intentionalClose = false;
    let sseErrorLogged = false;
    let hasTriedRefresh = false;
    const sseLifecycle = createSseReconnectHandler(SSE_CHANNELS.CLIENT_INTERVENTIONS);
    const sseMetrics = process.env.NODE_ENV !== 'production'
      ? (window.__ucpClientSseMetrics = window.__ucpClientSseMetrics || {
          startedAt: new Date().toISOString(),
          connectAttempts: 0,
          opens: 0,
          errors: 0,
          refreshAttempts: 0,
          refreshSuccess: 0,
          reconnectScheduled: 0,
          lastEventAt: null,
          lastErrorAt: null
        })
      : null;

    const bumpMetric = (name) => {
      if (!sseMetrics) return;
      sseMetrics[name] = (sseMetrics[name] || 0) + 1;
      sseMetrics.lastEventAt = new Date().toISOString();
    };

    const attemptTokenRefreshForSSE = async () => {
      bumpMetric('refreshAttempts');
      try {
        const response = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include'
        });

        if (!response.ok) {
          return false;
        }

        const payload = await response.json();
        if (!payload?.token) {
          return false;
        }

        if (!safeLocalStorageSetItem('token', payload.token)) {
          try { sessionStorage.setItem('token', payload.token); } catch { /* ignore */ }
        }
        if (payload.user) {
          window.dispatchEvent(new CustomEvent('ucp-auth-user-updated', { detail: payload.user }));
        }
        setAuthToken(payload.token);
        setSseCompatibilityCookie(payload.token);
        bumpMetric('refreshSuccess');
        return true;
      } catch (error) {
        return false;
      }
    };

    const connectSSE = () => {
      if (!isActive) return;
      try {
        // SSE s'authentifie via cookie ucp_token + withCredentials.
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) {
          devLog('[SSE] Pas de token — abandon SSE');
          return;
        }

        // Fallback de compatibilite dev: garantit le cookie attendu par EventSource.
        // Le cookie HttpOnly serveur reste la source principale en production.
        setSseCompatibilityCookie(token);

        intentionalClose = false;
        const streamUrl = process.env.NODE_ENV === 'production'
          ? '/api/client/interventions/stream'
          : `/api/client/interventions/stream?token=${encodeURIComponent(token)}`;
        bumpMetric('connectAttempts');
        eventSource = new EventSource(streamUrl, { withCredentials: true });

        // Reset du compteur uniquement lors d'une connexion RÉELLEMENT établie
        eventSource.onopen = () => {
          devLog('✅ [AuthContext] SSE connecté pour client:', user.id);
          retryCount = 0;
          sseErrorLogged = false;
          bumpMetric('opens');
          sseLifecycle.handleOpen();
        };

        // ✅ 04MAR2026: Notifications globales (toutes pages) pour changements statut
        eventSource.addEventListener('intervention-status-changed', (event) => {
          try {
            const data = JSON.parse(event.data);
            devLog('📡 [SSE] Statut intervention changé:', data);

            const typeMap = {
              planifiee:  { type: 'info',    title: i18n.t('toast.interventionPlanned'),    duration: 6000 },
              en_cours:   { type: 'info',    title: i18n.t('toast.interventionInProgress'), duration: 6000 },
              terminee:   { type: 'success', title: i18n.t('toast.interventionCompleted'),   duration: 8000 },
              annulee:    { type: 'warning', title: i18n.t('toast.interventionCancelled'),   duration: 6000 },
            };
            const cfg = typeMap[data.statut] || { type: 'info', title: i18n.t('toast.interventionUpdated'), duration: 5000 };

            const notification = {
              id: `status-${data.intervention_id}-${Date.now()}`,
              type: cfg.type,
              title: cfg.title,
              message: data.message || `"${data.titre}" — statut mis à jour`,
              timestamp: new Date(),
              duration: cfg.duration
            };

            pushNotification(notification);

            // Broadcast applicatif pour permettre aux pages d'actualiser leurs donnees
            // sans ouvrir une seconde connexion SSE sur le meme stream.
            if (typeof window !== 'undefined' && window.dispatchEvent) {
              window.dispatchEvent(new CustomEvent('ucp-client-intervention-status-changed', {
                detail: data
              }));
            }
          } catch (error) {
            console.error('❌ Erreur parsing SSE intervention-status-changed:', error);
          }
        });

        // Gestion erreurs SSE
        eventSource.onerror = async (error) => {
          if (!isActive || intentionalClose) {
            return;
          }

          sseLifecycle.handleError();

          // En arrière-plan : fermer pour forcer reconnect au réveil (GO_BG_SYNC).
          if (document.visibilityState === 'hidden') {
            if (eventSource) {
              try { eventSource.close(); } catch (_) { /* no-op */ }
              eventSource = null;
            }
            return;
          }

          if (!sseErrorLogged) {
            devLog('⚠️ [SSE] Erreur connexion (reconnexion automatique):', error);
            sseErrorLogged = true;
          }
          bumpMetric('errors');
          if (sseMetrics) sseMetrics.lastErrorAt = new Date().toISOString();
          if (eventSource) eventSource.close();
          eventSource = null;

          // Premier echec: tenter un refresh silencieux avant d'entrer dans la boucle de retries SSE.
          if (!hasTriedRefresh) {
            hasTriedRefresh = true;
            const refreshed = await attemptTokenRefreshForSSE();
            if (refreshed && isActive && !intentionalClose) {
              retryCount = 0;
              sseErrorLogged = false;
              connectSSE();
              return;
            }
          }

          retryCount++;

          if (retryCount >= 3) {
            if (process.env.NODE_ENV === 'production') {
              console.warn('⚠️ [SSE] 3 tentatives échouées — abandon SSE (session expirée ou route indisponible)');
            } else {
              devLog('⚠️ [SSE] 3 tentatives échouées — abandon SSE (session expirée ou route indisponible)');
            }
            return;
          }

          // Backoff exponentiel : 5s → 10s → 20s (max 60s)
          const delay = Math.min(5000 * Math.pow(2, retryCount - 1), 60000);
          devLog(`🔄 [SSE] Tentative ${retryCount}/3 dans ${delay}ms...`);
          bumpMetric('reconnectScheduled');
          retryTimer = setTimeout(connectSSE, delay);
        };

      } catch (error) {
        console.error('❌ Erreur création EventSource:', error);
      }
    };

    const forceRestart = () => {
      if (!isActive || intentionalClose) return;
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      retryCount = 0;
      hasTriedRefresh = false;
      sseErrorLogged = false;
      sseLifecycle.markDisconnected();
      if (eventSource) {
        try { eventSource.close(); } catch (_) { /* no-op */ }
        eventSource = null;
      }
      connectSSE();
      if (typeof window !== 'undefined' && window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('ucp-client-intervention-status-changed', {
          detail: { reason: 'app-resume' }
        }));
      }
    };

    connectSSE();
    window.addEventListener(APP_SYNC_EVENTS.SSE_FORCE_RESTART, forceRestart);

    // Cleanup: fermer la connexion SSE au démontage
    return () => {
      isActive = false;
      intentionalClose = true;
      window.removeEventListener(APP_SYNC_EVENTS.SSE_FORCE_RESTART, forceRestart);
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
      if (eventSource) {
        devLog('🔌 [SSE] Fermeture connexion');
        eventSource.close();
        eventSource = null;
      }
    };
  }, [user, pushNotification]);

  // Warmup offline global: précharge contrôlée (prod = sets terrain + délai + intervalle long).
  useEffect(() => {
    if (!user?.role) {
      return;
    }
    if (!isOfflineWarmupEnabled()) {
      return;
    }

    let disposed = false;
    let delayTimer = null;

    const runWarmup = async () => {
      if (disposed) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (typeof navigator !== 'undefined' && !navigator.onLine) return;
      await warmupOfflineData(user.role);
    };

    const startWarmup = () => {
      if (disposed) return;
      if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => {
          void runWarmup();
        }, { timeout: 8000 });
      } else {
        void runWarmup();
      }
    };

    const delayMs = getOfflineWarmupDelayMs();
    if (delayMs > 0) {
      delayTimer = setTimeout(startWarmup, delayMs);
    } else {
      startWarmup();
    }

    const intervalMs = getOfflineWarmupIntervalMs();
    const interval = intervalMs > 0 ? setInterval(runWarmup, intervalMs) : null;
    const handleOnline = () => startWarmup();

    window.addEventListener('online', handleOnline);

    return () => {
      disposed = true;
      if (delayTimer) clearTimeout(delayTimer);
      if (interval) clearInterval(interval);
      window.removeEventListener('online', handleOnline);
    };
  }, [user?.role]);

  const updateUserAvatar = useCallback((avatarUrl) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, avatar_url: avatarUrl || null };
      primeActorAvatarRegistry([{
        id: prev.id,
        role: prev.role,
        name: prev.name,
        email: prev.email,
        avatar_url: avatarUrl || null
      }]);
      const serialized = JSON.stringify(next);
      if (!safeLocalStorageSetItem('user', serialized)) {
        try { sessionStorage.setItem('user', serialized); } catch { /* ignore */ }
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ucp-auth-user-updated', { detail: { avatar_url: avatarUrl || null } }));
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const onUserUpdated = (event) => {
      const patch = event?.detail;
      if (!patch || typeof patch !== 'object') return;
      setUser((prev) => {
        if (!prev) return prev;
        const next = { ...prev, ...patch };
        const serialized = JSON.stringify(next);
        if (!safeLocalStorageSetItem('user', serialized)) {
          try { sessionStorage.setItem('user', serialized); } catch { /* ignore */ }
        }
        return next;
      });
    };
    window.addEventListener('ucp-auth-user-updated', onUserUpdated);
    return () => window.removeEventListener('ucp-auth-user-updated', onUserUpdated);
  }, []);

  const login = (userData, token) => {
    devLog('🔐 [AuthContext.login] userData received:', userData);
    devLog('🎨 [AuthContext.login] code_couleur:', userData?.code_couleur);
    
    if (!safeLocalStorageSetItem('token', token)) {
      try { sessionStorage.setItem('token', token); } catch { /* ignore */ }
    }
    const userSerialized = JSON.stringify(userData);
    if (!safeLocalStorageSetItem('user', userSerialized)) {
      try { sessionStorage.setItem('user', userSerialized); } catch { /* ignore */ }
    }
    
    devLog('💾 [AuthContext.login] Stored in localStorage:', {
      token: token.substring(0, 20) + '...',
      user: JSON.parse(localStorage.getItem('user'))
    });
    
    setUser(userData);
    setAuthToken(token);
    ensureActorAvatarProfilesPrimed(buildNotificationAvatarViewer(userData));
    // Compatibilité SSE: on pose aussi un cookie JS lisible pour sécuriser EventSource
    // quand le Set-Cookie backend n'est pas réinjecté côté frontend (proxy externe).
    setSseCompatibilityCookie(token);
  };

  const logout = useCallback(async (options = {}) => {
    if (logoutInProgressRef.current) return;
    logoutInProgressRef.current = true;
    try {
      const { sendMessagingPresenceOfflineKeepalive } = await import('./messagingService');
      sendMessagingPresenceOfflineKeepalive();
    } catch {
      /* no-op */
    }
    const { reason = null } = options;
    if (reason === 'inactivity') {
      sessionStorage.setItem('ucp_logout_reason', 'inactivity');
      sessionStorage.setItem('ucp_logout_at', String(Date.now()));
      safeLocalStorageSetItem('ucp_last_logout_reason', 'inactivity');
      safeLocalStorageSetItem('ucp_last_logout_at', String(Date.now()));
    }
    // ✅ SÉCURITÉ (02 MARS 2026) : Appeler le backend pour effacer le cookie HttpOnly
    // Seul le serveur peut effacer un cookie HttpOnly (document.cookie ne peut pas le faire)
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 2500);
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
        signal: controller.signal
      });
      clearTimeout(timeout);
    } catch (e) {
      // Ignorer les erreurs réseau — on déconnecte quand même localement
      console.warn('⚠️ [logout] Impossible d\'appeler /api/auth/logout:', e.message);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    clearOfflineQueueOnSessionEnd();
    localStorage.removeItem('statusCardsAnimationPlayed'); // Reset animation pour le prochain login
    clearUserPreferencesCache();
    resetActorAvatarProfilesPrime();
    applyUserPreferences({ theme: 'light', language: 'fr', notifications: true });
    setUser(null);
    setAuthToken(null);
    setNotifications([]); // ✅ Clear notifications au logout
    // FORCER la redirection vers la route de login publique
    const nextUrl = reason === 'inactivity'
      ? `/?logout_reason=inactivity&ts=${Date.now()}`
      : '/';
    window.location.href = nextUrl;
  }, []);

  // Renouvellement proactif du JWT tant que l'utilisateur est connecté (session jusqu'au logout manuel).
  useEffect(() => {
    if (!user) return undefined;

    const tick = async () => {
      try {
        await ensureFreshAccessToken({ bufferSec: JWT_REFRESH_BUFFER_SEC });
      } catch (error) {
        devLog('⚠️ [AuthContext] Renouvellement proactif échoué:', error?.message || error);
      }
    };

    tick();
    const intervalId = setInterval(tick, PROACTIVE_REFRESH_INTERVAL_MS);
    const uninstallBgSync = installBackgroundAppSyncWatchers();

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        tick();
      }
    };
    const onOnline = () => {
      tick();
    };
    const onPageShow = () => {
      tick();
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      clearInterval(intervalId);
      if (typeof uninstallBgSync === 'function') uninstallBgSync();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [user]);

  // Déconnexion automatique après inactivité — désactivée (déconnexion manuelle uniquement).
  useEffect(() => {
    if (!user || !AUTO_LOGOUT_ENABLED) {
      return;
    }

    let isDisposed = false;
    let watchdogInterval = null;
    let inactivityTimer = null;
    let lastActivityAt = Date.now();

    const triggerAutoLogout = () => {
      if (isDisposed) return;
      devLog('⏱️ [AuthContext] Déconnexion automatique après inactivité');
      logout({ reason: 'inactivity' });
    };

    const resetInactivityTimer = () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      lastActivityAt = Date.now();
      inactivityTimer = setTimeout(() => {
        triggerAutoLogout();
      }, INACTIVITY_TIMEOUT_MS);
    };

    const handleActivity = () => {
      resetInactivityTimer();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resetInactivityTimer();
      }
    };

    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'pointerdown', 'mousemove', 'wheel'];
    activityEvents.forEach((eventName) => window.addEventListener(eventName, handleActivity));
    window.addEventListener('focus', handleActivity);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    watchdogInterval = setInterval(() => {
      if (Date.now() - lastActivityAt >= INACTIVITY_TIMEOUT_MS) {
        triggerAutoLogout();
      }
    }, 10000);

    resetInactivityTimer();

    return () => {
      isDisposed = true;
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      if (watchdogInterval) {
        clearInterval(watchdogInterval);
      }
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, handleActivity));
      window.removeEventListener('focus', handleActivity);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, logout]);

  const value = {
    user,
    login,
    logout,
    updateUserAvatar,
    loading,
    notifications, // ✅ NOUVEAU: Exposer les notifications
    dismissNotification, // ✅ NOUVEAU: Fonction pour fermer une notification
    pushNotification
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
